// OWNER: AREA-OPS (dashboard, applications/*, suppressions, audit, system). Components go in src/features/ops.
// Senders: from identities and providers, including shared library senders, and the default.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { MoreHorizontal, Pencil, Plus, Send, Star, Trash2 } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { asJsonObject } from '@/api/json'
import { applicationQuery } from '@/api/queries/applications'
import type { Sender } from '@/api/types'
import { Can } from '@/auth/Can'
import { usePermissions } from '@/auth/usePermissions'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { DataTable, tableColumns } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { RelativeTime } from '@/components/RelativeTime'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { sendersQuery, useDeleteSender, useUpdateApplication } from '@/features/ops/api'
import { PROVIDER_LABEL, SenderDialog } from '@/features/ops/SenderDialog'

export const Route = createFileRoute('/applications/$appId/senders')({
  staticData: { crumb: 'Senders' },
  component: AppSendersPage,
})

interface Row extends Sender {
  isDefault: boolean
}

type Action = { kind: 'edit' | 'delete'; sender: Sender } | { kind: 'default'; sender: Sender }

function AppSendersPage() {
  const { appId } = Route.useParams()
  const app = useQuery(applicationQuery(appId))
  const senders = useQuery(sendersQuery(appId))
  const { can } = usePermissions()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<Sender | undefined>()
  const [deleting, setDeleting] = useState<Sender | undefined>()
  const del = useDeleteSender()
  const makeDefault = useUpdateApplication(appId, { successMessage: 'Default sender changed' })

  const defaultId = app.data?.default_sender_id ?? null
  const rows = useMemo<Row[]>(
    () => (senders.data ?? []).map((s) => ({ ...s, isDefault: s.id === defaultId })),
    [senders.data, defaultId],
  )
  const appOwned = rows.filter((r) => r.application_id).length

  const setDefaultSender = makeDefault.mutateAsync
  const act = useCallback(
    (a: Action) => {
      if (a.kind === 'edit') setEditing(a.sender)
      else if (a.kind === 'delete') setDeleting(a.sender)
      // Failures toast through the mutation cache.
      else void setDefaultSender({ default_sender_id: a.sender.id }).catch(() => undefined)
    },
    [setDefaultSender],
  )
  const canManage = can('manage_config')
  const columns = useMemo(() => buildColumns(act, canManage), [act, canManage])

  const defaultSender = rows.find((r) => r.isDefault)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-2">
          {!app.data || !senders.data ? (
            ' '
          ) : defaultSender ? (
            <>
              Rules without a sender use{' '}
              <span className="font-medium text-ink">{defaultSender.name}</span> (
              {defaultSender.from_email}).
            </>
          ) : (
            <span className="text-warning">
              No default sender. Every rule must name a sender until you choose one.
            </span>
          )}
        </p>
        <Can permission="manage_config" mode="disable">
          <Button variant="primary" icon={<Plus />} onClick={() => setAdding(true)}>
            Add sender
          </Button>
        </Can>
      </div>
      <DataTable
        aria-label="Senders"
        columnToggle={false}
        columns={columns}
        data={rows}
        getRowId={(s) => s.id}
        isLoading={senders.isPending || app.isPending}
        error={senders.error ?? app.error}
        onRetry={() => void senders.refetch()}
        initialSorting={[{ id: 'name', desc: false }]}
        onRowClick={can('manage_config') ? (s) => setEditing(s) : undefined}
        empty={
          <EmptyState
            icon={Send}
            title="No senders yet"
            description="A sender is the from address and delivery provider for this application’s email. Add one before you post events."
            action={
              can('manage_config') ? (
                <Button variant="primary" icon={<Plus />} onClick={() => setAdding(true)}>
                  Add sender
                </Button>
              ) : undefined
            }
          />
        }
      />
      <SenderDialog
        appId={appId}
        open={adding}
        onOpenChange={setAdding}
        isFirst={appOwned === 0 && !defaultId}
      />
      <SenderDialog
        appId={appId}
        sender={editing}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(undefined)}
      />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(undefined)}
        title={`Delete ${deleting?.name ?? 'sender'}?`}
        description={
          deleting?.id === defaultId
            ? 'This is the default sender. Rules that rely on the default will fail until you choose another.'
            : 'Rules that name this sender will fail to send. Messages already sent keep their history.'
        }
        confirmLabel="Delete sender"
        destructive
        onConfirm={() => deleting && del.mutateAsync(deleting.id)}
      />
    </div>
  )
}

const col = tableColumns<Row>()

function buildColumns(act: (a: Action) => void, canManage: boolean) {
  return col.columns([
    col.accessor('name', {
      header: 'Sender',
      cell: (c) => (
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium text-ink">{c.getValue()}</span>
          {c.row.original.isDefault && (
            <Badge tone="brass">
              <Star /> Default
            </Badge>
          )}
          {!c.row.original.application_id && <Badge tone="outline">Shared</Badge>}
        </div>
      ),
      enableHiding: false,
    }),
    col.accessor('from_email', {
      header: 'From',
      cell: (c) => (
        <div className="min-w-0">
          <div className="truncate">
            {c.row.original.from_name || <span className="text-ink-3">No name</span>}
          </div>
          <div className="truncate font-mono text-xs text-ink-2">{c.getValue()}</div>
        </div>
      ),
    }),
    col.accessor('reply_to', {
      header: 'Reply-to',
      cell: (c) =>
        c.getValue() ? (
          <span className="font-mono text-xs">{c.getValue()}</span>
        ) : (
          <span className="text-ink-3">—</span>
        ),
    }),
    col.accessor('provider', {
      header: 'Provider',
      cell: (c) => {
        const cfg = asJsonObject(c.row.original.provider_config)
        const set = typeof cfg.configuration_set === 'string' ? cfg.configuration_set : undefined
        return (
          <div className="min-w-0">
            <Badge tone={c.getValue() === 'log' ? 'warning' : 'neutral'}>
              {PROVIDER_LABEL[c.getValue()]}
            </Badge>
            {c.getValue() === 'ses' && (
              <div className="mt-0.5 truncate text-xs text-ink-3">
                {set ? (
                  <>
                    Config set <span className="font-mono">{set}</span>
                  </>
                ) : (
                  'Default config set'
                )}
              </div>
            )}
          </div>
        )
      },
      meta: { width: '11rem' },
    }),
    col.accessor('updated_at', {
      header: 'Updated',
      cell: (c) => <RelativeTime value={c.getValue()} className="text-ink-2" />,
      sortFn: 'datetime',
      meta: { align: 'right', width: '8rem' },
    }),
    col.display({
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: (c) =>
        canManage ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton label={`Actions for ${c.row.original.name}`} size="sm">
                <MoreHorizontal />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => act({ kind: 'edit', sender: c.row.original })}>
                <Pencil /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={c.row.original.isDefault}
                onSelect={() => act({ kind: 'default', sender: c.row.original })}
              >
                <Star /> Make default
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                destructive
                onSelect={() => act({ kind: 'delete', sender: c.row.original })}
              >
                <Trash2 /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null,
      enableHiding: false,
      meta: { align: 'right', width: '3.5rem', stopRowClick: true, label: '' },
    }),
  ])
}
